import type { SentryConfig } from '@/types';
import * as Sentry from '@sentry/react';

export function initSentry(config: SentryConfig) {
    if (!config?.dsn) {
        return;
    }

    Sentry.init({
        dsn: config.dsn,
        environment: config.environment,
        release: config.release,

        // Performance monitoring
        tracesSampleRate: 0.1,

        // Session replay for debugging
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],

        // Filter out noisy errors
        ignoreErrors: [
            // Browser extensions
            /extensions\//i,
            /^chrome-extension:\/\//,
            /webkit-masked-url/i,
            /cashback/i,
            /coupon/i,
            /sseError/i,
            // Network errors that are usually user-side
            'Network request failed',
            'Failed to fetch',
            'Load failed',
            // ResizeObserver errors (common and harmless)
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
        ],

        // Filter events from browser extensions based on stack trace
        beforeSend(event, hint) {
            // Check if error originated from a browser extension
            const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
            const isFromExtension = frames.some(frame => {
                const filename = frame.filename || '';
                return (
                    filename.includes('chrome-extension://') ||
                    filename.includes('moz-extension://') ||
                    filename.includes('safari-extension://') ||
                    filename.includes('webkit-masked-url') ||
                    filename.includes('/extensions/')
                );
            });

            if (isFromExtension) {
                return null; // Don't send to Sentry
            }

            return event;
        },
    });
}

// Re-export Sentry for use throughout the app
export { Sentry };
